/**
 * Pure decision logic for the stale-chunk auto-reload, split out from
 * `vite-preload-recovery` so it can be unit-tested without a DOM or Vite env.
 *
 * After a deploy, a client holding an old page can request a hash-named chunk
 * that no longer exists (404). We reload once to pick up fresh assets — but a
 * single reload is not always enough: during a zero-downtime deploy the old and
 * new instances serve traffic simultaneously and Traefik round-robins between
 * them, so a reload can land back on the version that lacks the chunk and 404
 * again. A one-shot guard therefore gives up too early and leaves a blank
 * screen. Instead we allow a small, time-boxed budget of reloads with a short
 * backoff so the client rides through the brief overlap window, then stops so a
 * genuinely broken deploy surfaces a recoverable fallback instead of looping.
 */

/** Persisted across reloads (in sessionStorage) to bound the retry budget. */
export type StaleChunkReloadRecord = {
  count: number;
  firstAt: number;
};

export type StaleChunkReloadDecision = {
  /** Whether the caller should reload the page now. */
  shouldReload: boolean;
  /** How long to wait before reloading, in ms (backoff). 0 means reload now. */
  delayMs: number;
  /** Record to persist for the next attempt (unchanged when giving up). */
  nextRecord: StaleChunkReloadRecord | null;
};

/** Max reloads allowed inside a single window before giving up. */
export const STALE_CHUNK_MAX_RELOADS = 3;
/** Length of the retry window; a later, unrelated deploy gets a fresh budget. */
export const STALE_CHUNK_WINDOW_MS = 30_000;
/** Extra wait added per attempt so we don't hammer during the deploy overlap. */
export const STALE_CHUNK_BACKOFF_STEP_MS = 800;

export type StaleChunkReloadOptions = {
  maxReloads?: number;
  windowMs?: number;
  backoffStepMs?: number;
};

export function decideStaleChunkReload(
  record: StaleChunkReloadRecord | null,
  now: number,
  options: StaleChunkReloadOptions = {},
): StaleChunkReloadDecision {
  const maxReloads = options.maxReloads ?? STALE_CHUNK_MAX_RELOADS;
  const windowMs = options.windowMs ?? STALE_CHUNK_WINDOW_MS;
  const backoffStepMs = options.backoffStepMs ?? STALE_CHUNK_BACKOFF_STEP_MS;

  // A record only counts while its window is still open. Once it has elapsed we
  // start over, so a deploy hours into a long-lived tab is not blocked by a
  // budget that a much earlier deploy already spent.
  const windowOpen = record !== null && now - record.firstAt < windowMs;
  const priorCount = windowOpen ? record.count : 0;
  const firstAt = windowOpen ? record.firstAt : now;

  if (priorCount >= maxReloads) {
    return { shouldReload: false, delayMs: 0, nextRecord: record };
  }

  const nextCount = priorCount + 1;

  return {
    shouldReload: true,
    delayMs: (nextCount - 1) * backoffStepMs,
    nextRecord: { count: nextCount, firstAt },
  };
}
